import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Zap, BookOpen, Map, Menu, X, LogOut, User, MapPin, Search, PlusCircle, Columns2 } from 'lucide-react';
import { useNavigationStore } from '../../store/useNavigationStore';
import { CruxLogo } from '../Logo/CruxLogo';
import { useAuth } from '../../contexts/AuthContext';
import { useMarketStore, MARKETS } from '../../store/useMarketStore';

export function Navigation() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [userMenuOpen, setUserMenuOpen] = React.useState(false);
  const setCurrentRoute = useNavigationStore((state) => state.setCurrentRoute);
  const { user, logout } = useAuth();
  const activeMarket = useMarketStore((s) => s.activeMarket);
  const setActiveMarket = useMarketStore((s) => s.setActiveMarket);

  React.useEffect(() => {
    setCurrentRoute(location.pathname);
  }, [location.pathname, setCurrentRoute]);

  const navItems = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/leads', label: 'Lead Pipeline', icon: Zap },
    { path: '/source', label: 'Source Deals', icon: Search },
    { path: '/deal/new', label: 'New Deal', icon: PlusCircle },
    { path: '/playbook', label: 'Playbook', icon: BookOpen },
    { path: '/zandi-map', label: 'Submarkets', icon: Map },
    { path: '/compare', label: 'Compare Markets', icon: Columns2 },
  ];

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <nav className="bg-charcoal border-b border-slate-dark sticky top-0 z-50 shadow-sm">
      <div className="max-w-[2000px] mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo/Brand */}
          <Link to="/" className="flex items-center">
            <CruxLogo variant="horizontal" size="sm" showSubtitle theme="light" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors relative ${
                    active
                      ? 'text-gold bg-graphite'
                      : 'text-stone-500 hover:text-ivory hover:bg-graphite'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{item.label}</span>
                  {active && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gold rounded-full" />
                  )}
                </Link>
              );
            })}
          </div>

          {/* Market Selector + User Menu */}
          <div className="hidden md:flex items-center gap-3 relative">
            {/* Market Selector */}
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-graphite rounded-lg border border-slate-dark">
              <MapPin className="w-3.5 h-3.5 text-gold" />
              <select
                value={activeMarket.market_id}
                onChange={(e) => setActiveMarket(e.target.value)}
                className="bg-transparent text-sm font-medium text-ivory border-none outline-none cursor-pointer appearance-none pr-4"
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23a8a29e' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0 center' }}
              >
                {MARKETS.map((m) => (
                  <option key={m.market_id} value={m.market_id}>
                    {m.display_name}, {m.state}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* User Menu */}
          <div className="hidden md:flex items-center gap-3 relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-graphite transition-colors"
            >
              <div className="w-8 h-8 bg-graphite rounded-full flex items-center justify-center">
                <User className="w-4 h-4 text-gold" />
              </div>
              <span className="text-sm font-medium text-stone-600">
                {user?.name || user?.email?.split('@')[0] || 'User'}
              </span>
            </button>

            {/* Dropdown Menu */}
            {userMenuOpen && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-charcoal rounded-lg shadow-lg border border-slate-dark py-1 z-50">
                <div className="px-4 py-2 border-b border-slate-dark">
                  <p className="text-sm font-medium text-ivory">{user?.name}</p>
                  <p className="text-xs text-stone-500">{user?.email}</p>
                  <p className="text-xs text-gold capitalize mt-1">{user?.role}</p>
                </div>
                <button
                  onClick={() => {
                    setUserMenuOpen(false);
                    logout();
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-stone-600 hover:bg-graphite transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Sign out</span>
                </button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-graphite transition-colors"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-ivory" />
            ) : (
              <Menu className="w-6 h-6 text-ivory" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-dark py-4">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                    active
                      ? 'text-gold bg-graphite'
                      : 'text-stone-500 hover:text-ivory hover:bg-graphite'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </nav>
  );
}
